import axios from "axios";
import { getGuestToken } from "../utils/guestScans";

// Base URL for the API
// This should point to our Flask backend running on port 3001
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// Create an axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Important for session handling
  timeout: 120000, // 2 minutes timeout for most requests
});

// A scan started without logging in is read with the secret token it was created with.
// Only the read-only endpoints accept it, so it is only ever sent to them.
const GUEST_READABLE_PATH = /^\/scans\/([^/?#]+)\/(progress|results)\/?(\?.*)?$/;
apiClient.interceptors.request.use((config) => {
  const match = GUEST_READABLE_PATH.exec(config.url || "");
  if (match) {
    const token = getGuestToken(decodeURIComponent(match[1]));
    if (token) {
      config.headers["X-Scan-Token"] = token;
    }
  }
  return config;
});

/**
 * Sanitize a path parameter to prevent path traversal attacks
 * @param {string} param - The parameter to sanitize
 * @returns {string} - Sanitized and URL-encoded parameter
 * @throws {Error} - If parameter contains invalid characters
 */
const sanitizePathParam = (param) => {
  if (!param || typeof param !== 'string') {
    throw new Error('Invalid parameter: must be a non-empty string');
  }

  // Check for path traversal patterns
  if (param.includes('..') || param.includes('/') || param.includes('\\')) {
    throw new Error('Invalid parameter: contains path traversal characters');
  }

  // Check for URL special characters that could modify the request
  if (param.includes('?') || param.includes('#') || param.includes('&')) {
    throw new Error('Invalid parameter: contains URL special characters');
  }

  // Return URL-encoded parameter
  return encodeURIComponent(param);
};

// API service functions
const securityApi = {
  /**
   * Connect to a repository and initiate a scan
   * @param {string} repoUrl - URL of the repository to scan
   * @param {boolean} isPrivate - Whether the repository is private
   * @param {number} installationId - Optional GitHub App installation ID
   * @returns {Promise} - Promise with the scan initialization data
   */
  connectRepository: async (repoUrl, isPrivate = false, installationId = null) => {
    try {
      // Call the Python backend endpoint to start a scan
      const response = await apiClient.post('/scans', { 
        repoUrl, 
        isPrivate,
        installationId 
      });
      return response.data;
    } catch (error) {
      console.error("Error connecting repository:", error);
      throw error;
    }
  },

  /**
   * Start a scan of a public repository without logging in
   * @param {string} repoUrl - URL of the public GitHub repository
   * @returns {Promise} - {scanId, status, queuePosition, accessToken, repoUrl}
   */
  startAnonymousScan: async (repoUrl) => {
    try {
      const response = await apiClient.post('/public/scans', { repoUrl });
      return response.data;
    } catch (error) {
      console.error("Error starting guest scan:", error);
      throw error;
    }
  },

  /**
   * Attach scans started before logging in to the current user
   * @param {Array<{scanId: string, token: string}>} guestScans - Scans and their access tokens
   * @returns {Promise} - {claimed: [scanId, ...]}
   */
  claimAnonymousScans: async (guestScans) => {
    try {
      const response = await apiClient.post('/scans/claim', {
        scans: guestScans.map(({ scanId, token }) => ({ scanId, accessToken: token })),
      });
      return response.data;
    } catch (error) {
      console.error("Error claiming guest scans:", error);
      throw error;
    }
  },

  /**
   * Get the current scan progress
   * @param {string} scanId - ID of the current scan
   * @param {number} lastLogId - ID of the last log received (optional)
   * @returns {Promise} - Promise with the scan progress data
   */
  getScanProgress: async (scanId, lastLogId = 0) => {
    try {
      // Sanitize scanId to prevent path traversal attacks
      const safeScanId = sanitizePathParam(scanId);

      // Get scan progress from the Python backend
      // Use shorter timeout for progress endpoint (should be fast)
      const response = await apiClient.get(`/scans/${safeScanId}/progress`, {
        params: { lastLogId },
        timeout: 30000, // 30 seconds timeout for progress endpoint
      });
      return response.data;
    } catch (error) {
      console.error("Error getting scan progress:", error);
      throw error;
    }
  },

  /**
   * Get the scan results
   * @param {string} scanId - ID of the completed scan
   * @param {Object} options - Pagination and filter options
   * @returns {Promise} - Promise with the scan results data
   */
  getScanResults: async (scanId, options = {}) => {
    try {
      // Sanitize scanId to prevent path traversal attacks
      const safeScanId = sanitizePathParam(scanId);

      const {
        limit = 25,
        offset = 0,
        category = null,
        severity = null,
        status = null,
        sortBy = 'severity',
        sortOrder = 'desc'
      } = options;

      // Build params object for axios
      const params = {
        limit,
        offset,
        sort_by: sortBy,
        sort_order: sortOrder,
        generate_titles: 'false'  // Disable title generation to prevent timeouts
      };

      // Only add filter params if they have values
      if (category) params.category = category;
      if (severity) params.severity = severity;
      if (status) params.status = status;

      // Get scan results from the Python backend
      // Use longer timeout for results (may contain many vulnerabilities)
      const response = await apiClient.get(`/scans/${safeScanId}/results`, {
        params,
        timeout: 480000, // 8 minutes for large scans with many vulnerabilities
      });
      return response.data;
    } catch (error) {
      console.error("Error getting scan results:", error);
      throw error;
    }
  },

  /**
   * Get a specific vulnerability by ID
   * @param {string} scanId - ID of the scan
   * @param {string} vulnId - ID of the vulnerability
   * @returns {Promise} - Promise with the vulnerability data
   */
  getVulnerabilityById: async (scanId, vulnId) => {
    try {
      // Sanitize path parameters to prevent path traversal attacks
      const safeScanId = sanitizePathParam(scanId);
      const safeVulnId = sanitizePathParam(vulnId);

      const response = await apiClient.get(`/scans/${safeScanId}/vulnerabilities/${safeVulnId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting vulnerability:", error);
      throw error;
    }
  },

  /**
   * Apply an auto-fix to a vulnerability
   * @param {string} scanId - ID of the scan
   * @param {number} vulnerabilityId - ID of the vulnerability to fix
   * @returns {Promise} - Promise with the fix result
   */
  applyFix: async (scanId, vulnerabilityId) => {
    try {
      // Sanitize path parameters to prevent path traversal attacks
      const safeScanId = sanitizePathParam(scanId);
      const safeVulnId = sanitizePathParam(String(vulnerabilityId));

      // Apply fix through the Python backend
      const response = await apiClient.post(
        `/scans/${safeScanId}/vulnerabilities/${safeVulnId}/fix`
      );
      return response.data;
    } catch (error) {
      console.error("Error applying fix:", error);
      throw error;
    }
  },

  /**
   * Get the current fix progress for a vulnerability
   * @param {string} scanId - ID of the scan
   * @param {number} vulnerabilityId - ID of the vulnerability being fixed
   * @param {number} lastLogId - ID of the last log received (optional)
   * @returns {Promise} - Promise with the fix progress data
   */
  getFixProgress: async (scanId, vulnerabilityId, lastLogId = 0) => {
    try {
      // Sanitize path parameters to prevent path traversal attacks
      const safeScanId = sanitizePathParam(scanId);
      const safeVulnId = sanitizePathParam(String(vulnerabilityId));

      // Get fix progress from the Python backend
      const response = await apiClient.get(
        `/scans/${safeScanId}/vulnerabilities/${safeVulnId}/progress`,
        {
          params: { lastLogId },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting fix progress:", error);
      throw error;
    }
  },

  /**
   * Generate a security report for a scan
   * @param {string} scanId - ID of the scan
   * @param {Object} options - Report generation options
   * @returns {Promise} - Promise with the report data or download URL
   */
  generateReport: async (scanId, options = {}) => {
    try {
      const { format = 'pdf', reportType = 'technical', includeCharts = true, branding = null } = options;

      // Sanitize scanId to prevent path traversal attacks
      const safeScanId = sanitizePathParam(scanId);

      // Generate report through the Python backend
      const response = await apiClient.post(`/scans/${safeScanId}/report`, {
        format,
        report_type: reportType,
        include_charts: includeCharts,
        branding
      }, {
        responseType: 'json', // always JSON; server returns download_url or inline content
        timeout: 300000, // 5 minutes for report generation
      });

      const data = response.data;

      // Server saved a file artifact (PDF on disk) — return its URL
      if (data.download_url) {
        return { downloadUrl: data.download_url, format: data.format };
      }

      // Server returned inline content (HTML, Markdown, or PDF fallback to HTML)
      return { data: data.content, format: data.format };
    } catch (error) {
      console.error("Error generating report:", error);
      throw error;
    }
  },

  /**
   * Check the health of the API
   * @returns {Promise} - Promise with the health check data
   */
  healthCheck: async () => {
    try {
      const response = await apiClient.get("/health");
      return response.data;
    } catch (error) {
      console.error("Error checking API health:", error);
      throw error;
    }
  },

  /**
   * Login with google
   * @returns {Promise} - Promise that redirects to GitHub OAuth
   */
  loginWithGoogle: async () => {
    try {
      window.location.href = `${API_BASE_URL}/auth/login`;
    } catch (error) {
      console.error("Error initiating GitHub login:", error);
      throw error;
    }
  },

  /**
   * Login with GitHub
   * @returns {Promise} - Promise that redirects to GitHub OAuth
   */
  loginWithGithub: async () => {
    try {
      window.location.href = `${API_BASE_URL}/auth/github/login`;
    } catch (error) {
      console.error("Error initiating GitHub login:", error);
      throw error;
    }
  },

  /**
   * Logout the current user
   * @returns {Promise} - Promise that redirects to the login page
   */
  logout: async () => {
    try {
      await apiClient.get('/auth/logout');
      // After successful logout, redirect to frontend login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during logout:', error);
      throw error;
    }
  },

  // Add or update these compliance-related API functions


  /**
   * Get suggested compliance questions
   * @param {string} repoPath - Path to the repository
   * @returns {Promise} - Promise with suggested questions
   */
  getSuggestedQuestions: async (repoPath) => {
    try {
      const response = await apiClient.post("/compliance/suggested-questions", {
        repo_path: repoPath,
        count: 7,
        include_general: true,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting suggested questions:", error);
      throw error;
    }
  },

  /**
   * Get compliance question history
   * @param {string} repoPath - Path to the repository
   * @returns {Promise} - Promise with question history
   */
  getQuestionHistory: async (repoPath) => {
    try {
      const response = await apiClient.get("/compliance/history", {
        params: { repo_path: repoPath },
      });
      return response.data;
    } catch (error) {
      console.error("Error getting question history:", error);
      throw error;
    }
  },

  /**
   * Ask a compliance question
   * @param {string} question - The question to ask
   * @param {string} repoPath - Path to the repository
   * @returns {Promise} - Promise with the answer
   */
  askComplianceQuestion: async (question, repoPath) => {
    try {
      const response = await apiClient.post("/compliance/ask", {
        question,
        repo_path: repoPath,
      });
      return response.data;
    } catch (error) {
      console.error("Error asking compliance question:", error);
      throw error;
    }
  },

  // =============================================
  // Compliance Project Management APIs (Issue #5)
  // =============================================

  /**
   * Create a new compliance project
   * @param {Object} projectData - Project configuration
   * @param {string} projectData.name - Project name
   * @param {string} projectData.description - Optional description
   * @param {string} projectData.frameworkId - Framework UUID (e.g., SOC 2 Type I)
   * @param {Array<string>} projectData.tscCategories - TSC categories to include
   * @param {string} projectData.targetAuditDate - Target audit date (YYYY-MM-DD)
   * @returns {Promise} - Promise with created project data
   */
  createComplianceProject: async (projectData) => {
    try {
      const response = await apiClient.post('/compliance/projects', {
        name: projectData.name,
        description: projectData.description || null,
        framework_id: projectData.frameworkId,
        tsc_categories: projectData.tscCategories,
        target_audit_date: projectData.targetAuditDate || null,
      });
      return response.data;
    } catch (error) {
      console.error("Error creating compliance project:", error);
      throw error;
    }
  },

  /**
   * Get a compliance project by ID
   * @param {string} projectId - Project UUID
   * @returns {Promise} - Promise with project details including tasks
   */
  getComplianceProject: async (projectId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const response = await apiClient.get(`/compliance/projects/${safeProjectId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting compliance project:", error);
      throw error;
    }
  },

  /**
   * List all compliance projects for the current user's organization
   * @returns {Promise} - Promise with list of projects
   */
  listComplianceProjects: async () => {
    try {
      const response = await apiClient.get('/compliance/projects');
      return response.data;
    } catch (error) {
      console.error("Error listing compliance projects:", error);
      throw error;
    }
  },

  /**
   * Get integration status for a compliance project
   * @param {string} projectId - Project UUID
   * @returns {Promise} - Promise with integration statuses (AWS, GWS, GitHub)
   */
  getProjectIntegrations: async (projectId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const response = await apiClient.get(`/compliance/projects/${safeProjectId}/integrations`);
      return response.data;
    } catch (error) {
      console.error("Error getting project integrations:", error);
      throw error;
    }
  },

  /**
   * Trigger initial compliance scan for all connected integrations
   * @param {string} projectId - Project UUID
   * @param {Array<string>} integrationTypes - Optional specific integrations to scan
   * @returns {Promise} - Promise with scan session info
   */
  triggerInitialScan: async (projectId, integrationTypes = null) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const payload = integrationTypes ? { integration_types: integrationTypes } : {};
      const response = await apiClient.post(
        `/compliance/projects/${safeProjectId}/initial-scan`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error triggering initial scan:", error);
      throw error;
    }
  },

  /**
   * Get gap analysis for a compliance project
   * @param {string} projectId - Project UUID
   * @returns {Promise} - Promise with gap analysis data
   */
  getGapAnalysis: async (projectId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const response = await apiClient.get(`/compliance/projects/${safeProjectId}/gap-analysis`);
      return response.data;
    } catch (error) {
      console.error("Error getting gap analysis:", error);
      throw error;
    }
  },

  /**
   * Initiate AWS integration connection (legacy access key method)
   * Redirects to AWS credential input flow
   * @param {string} returnUrl - URL to return to after connection
   * @deprecated Use initiateAWSSSOConnection for SSO-based authentication
   */
  initiateAWSConnection: (returnUrl = '/compliance/new') => {
    window.location.href = `${API_BASE_URL}/integrations/aws/connect?return_url=${encodeURIComponent(returnUrl)}`;
  },

  /**
   * Initiate AWS SSO connection using IAM Identity Center
   * Returns device authorization info for user authentication
   * @param {string} name - Friendly name for the integration
   * @param {string} ssoStartUrl - AWS SSO start URL (e.g., https://my-company.awsapps.com/start)
   * @param {string} ssoRegion - AWS region for SSO (default: us-east-1)
   * @param {string} returnUrl - URL to return to after connection
   * @returns {Promise} - Promise with verification URL and state
   */
  initiateAWSSSOConnection: async (name, ssoStartUrl, ssoRegion = 'us-east-1', returnUrl = '/compliance/new') => {
    try {
      const params = new URLSearchParams({
        name,
        sso_start_url: ssoStartUrl,
        sso_region: ssoRegion,
        return_url: returnUrl,
      });
      const response = await apiClient.get(`/integrations/aws/sso/connect?${params}`);
      return response.data;
    } catch (error) {
      console.error("Error initiating AWS SSO connection:", error);
      throw error;
    }
  },

  /**
   * Poll for AWS SSO authentication status
   * Call this repeatedly after user visits the verification URL
   * @param {string} state - State token from initiateAWSSSOConnection
   * @returns {Promise} - Promise with status, message, and optionally integration_id
   */
  pollAWSSSOStatus: async (state) => {
    try {
      const response = await apiClient.post('/integrations/aws/sso/poll', { state });
      return response.data;
    } catch (error) {
      console.error("Error polling AWS SSO status:", error);
      throw error;
    }
  },

  /**
   * List AWS accounts available via SSO
   * @param {string} integrationId - Integration UUID
   * @returns {Promise} - Promise with list of accounts and roles
   */
  listAWSSSOAccounts: async (integrationId) => {
    try {
      const safeIntegrationId = sanitizePathParam(integrationId);
      const response = await apiClient.get(`/integrations/aws/sso/accounts/${safeIntegrationId}`);
      return response.data;
    } catch (error) {
      console.error("Error listing AWS SSO accounts:", error);
      throw error;
    }
  },

  /**
   * Select an AWS account and role for an SSO integration
   * @param {string} integrationId - Integration UUID
   * @param {string} accountId - AWS account ID
   * @param {string} roleName - Name of the role to assume
   * @returns {Promise} - Promise with success status
   */
  selectAWSSSORole: async (integrationId, accountId, roleName) => {
    try {
      const safeIntegrationId = sanitizePathParam(integrationId);
      const params = new URLSearchParams({
        account_id: accountId,
        role_name: roleName,
      });
      const response = await apiClient.post(`/integrations/aws/sso/select-role/${safeIntegrationId}?${params}`);
      return response.data;
    } catch (error) {
      console.error("Error selecting AWS SSO role:", error);
      throw error;
    }
  },

  /**
   * Cancel an in-progress AWS SSO authentication flow
   * @param {string} state - State token to cancel
   * @returns {Promise} - Promise with cancellation status
   */
  cancelAWSSSOFlow: async (state) => {
    try {
      const response = await apiClient.delete(`/integrations/aws/sso/states/${encodeURIComponent(state)}`);
      return response.data;
    } catch (error) {
      console.error("Error cancelling AWS SSO flow:", error);
      throw error;
    }
  },

  /**
   * Initiate Google Workspace OAuth connection
   * @param {string} returnUrl - URL to return to after OAuth
   */
  initiateGWSConnection: (returnUrl = '/compliance/new') => {
    window.location.href = `${API_BASE_URL}/integrations/google-workspace/connect?return_url=${encodeURIComponent(returnUrl)}`;
  },

  /**
   * Get list of GitHub repositories
   * @returns {Promise} - Promise with the list of repositories
   */
  getGithubRepositories: async () => {
    try {
      const response = await apiClient.get("/import_repository_github", {
        params: {
          per_page: 100,
          sort: "updated",
          direction: "desc",
        },
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        // Handle authentication error
        throw new Error("GitHub authentication required");
      }
      console.error("Error fetching GitHub repositories:", error);
      throw error;
    }
  },

  /**
   * Get GitHub authentication status
   * @returns {Promise} - Promise with auth status
   */
  checkGithubAuth: async () => {
    try {
      const response = await apiClient.get("/auth/github/status");
      return response.data;
    } catch (error) {
      console.error("Error checking GitHub auth status:", error);
      // If the endpoint doesn't exist or returns an error, assume not authenticated
      return { authenticated: false };
    }
  },

  /**
   * Initiate GitHub OAuth flow
   * @param {boolean} forceLogin - Whether to force the login screen even if already logged in
   */
  initiateGithubAuth: (forceLogin = false) => {
    window.location.href = `${API_BASE_URL}/auth/github/login${forceLogin ? '?force_login=true' : ''}`;
  },

  /**
   * Get GitHub App information
   * @returns {Promise} - Promise with GitHub App info
   */
  getGithubAppInfo: async () => {
    try {
      const response = await apiClient.get("/github/app/info");
      return response.data;
    } catch (error) {
      console.error("Error getting GitHub App info:", error);
      throw error;
    }
  },

  /**
   * Get GitHub App installations
   * @returns {Promise} - Promise with GitHub App installations
   */
  getGithubAppInstallations: async () => {
    try {
      const response = await apiClient.get("/github/app/installations");
      return response.data;
    } catch (error) {
      console.error("Error getting GitHub App installations:", error);
      throw error;
    }
  },

  /**
   * Get repositories accessible to the GitHub App
   * @param {number} installationId - Optional installation ID to filter repositories
   * @returns {Promise} - Promise with repositories list
   */
  getGithubAppRepositories: async (installationId = null) => {
    try {
      const params = installationId ? { installation_id: installationId } : {};
      const response = await apiClient.get("/github/app/repositories", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting GitHub App repositories:", error);
      throw error;
    }
  },

  /**
   * Delete/remove a GitHub App installation record
   * @param {number} installationId - The installation ID to remove
   * @returns {Promise} - Promise with deletion result
   */
  deleteGithubAppInstallation: async (installationId) => {
    const id = Number(installationId);
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error('Invalid installation ID');
    }
    try {
      const response = await apiClient.delete(`/github/app/installations/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting GitHub App installation:", error);
      throw error;
    }
  },

  /**
   * Update repository permissions for a GitHub App installation
   * @param {number} installationId - Installation ID
   * @param {Array} repositoryPermissions - Array of repository permissions with id and selected status
   * @returns {Promise} - Promise with update result
   */
  updateGithubAppRepositoryPermissions: async (installationId, repositoryPermissions) => {
    try {
      const response = await apiClient.post('/github/app/repositories/permissions', {
        installation_id: installationId,
        repository_permissions: repositoryPermissions
      });
      return response.data;
    } catch (error) {
      console.error('Error updating GitHub App repository permissions:', error);
      throw error;
    }
  },

  /**
   * Get dashboard statistics
   * @param {string} startDate - Optional start date in YYYY-MM-DD format
   * @param {string} endDate - Optional end date in YYYY-MM-DD format
   * @returns {Promise} - Promise with dashboard statistics
   */
  getDashboardStats: async (startDate = null, endDate = null) => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await apiClient.get("/dashboard/stats", { params });
      return response.data;
    } catch (error) {
      console.error("Error getting dashboard stats:", error);
      throw error;
    }
  },

  /**
   * Get latest security findings for dashboard
   * @param {number} limit - Number of findings to return
   * @param {string} startDate - Optional start date in YYYY-MM-DD format
   * @param {string} endDate - Optional end date in YYYY-MM-DD format
   * @returns {Promise} - Promise with latest findings data
   */
  getLatestFindings: async (limit = 5, startDate = null, endDate = null) => {
    try {
      const params = { limit };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await apiClient.get("/dashboard/latest-findings", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting latest findings:", error);
      throw error;
    }
  },

  /**
   * Get pull request metrics over time
   * @param {string} startDate - Optional start date in YYYY-MM-DD format
   * @param {string} endDate - Optional end date in YYYY-MM-DD format
   * @param {string} interval - Grouping interval: 'day', 'week', or 'month'
   * @returns {Promise} - Promise with PR metrics data
   */
  getPRMetrics: async (startDate = null, endDate = null, interval = "day") => {
    try {
      const params = { interval };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await apiClient.get("/dashboard/pr-metrics", { params });
      return response.data;
    } catch (error) {
      console.error("Error getting PR metrics:", error);
      throw error;
    }
  },

  /**
   * Get north-star health metrics for the security-lead dashboard: PR merge rate
   * (with a health threshold), median time-to-merge, and open-PR count. These
   * measure how fast issues get fixed and how often our PRs are accepted, rather
   * than raw findings count.
   * @param {string} startDate - Optional ISO date (YYYY-MM-DD)
   * @param {string} endDate - Optional ISO date (YYYY-MM-DD)
   * @returns {Promise} - Promise with health metrics
   */
  getDashboardHealthMetrics: async (startDate = null, endDate = null) => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await apiClient.get("/dashboard/health-metrics", { params });
      return response.data;
    } catch (error) {
      console.error("Error getting dashboard health metrics:", error);
      throw error;
    }
  },

  /**
   * Get statistics for the current user's repositories.
   * @returns {Promise} - Promise with user-specific statistics
   */
  getUserStats: async () => {
    try {
      const response = await apiClient.get("/user/stats");
      return response.data;
    } catch (error) {
      console.error("Error getting user stats:", error);
      throw error;
    }
  },

  /**
   * Get list of all repositories
   * @returns {Promise} - Promise with the list of repositories
   */
  getRepositories: async () => {
    try {
      const response = await apiClient.get('/repositories');
      return response.data;
    } catch (error) {
      console.error('Error fetching repositories:', error);
      throw error;
    }
  },

  /**
   * Get scan history for a specific repository
   * @param {string} repoUrl - URL of the repository
   * @returns {Promise} - Promise with the scan history
   */
  getRepositoryScans: async (repoUrl) => {
    try {
      const response = await apiClient.get(`/repositories/scans?repo_url=${encodeURIComponent(repoUrl)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching repository scans:', error);
      throw error;
    }
  },

  /**
   * Get statistics for a specific repository
   * @param {string} repoUrl - URL of the repository
   * @returns {Promise} - Promise with the repository statistics
   */
  getRepositoryStats: async (repoUrl) => {
    try {
      const response = await apiClient.get(`/repositories/stats?repo_url=${encodeURIComponent(repoUrl)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching repository stats:', error);
      throw error;
    }
  },

  /**
   * Get all scans across all repositories (client-side aggregation)
   *
   * TODO: Replace with dedicated backend endpoint GET /api/scans
   *
   * KNOWN LIMITATIONS (N+1 query pattern):
   * - Makes 1 + N network requests where N = number of repositories
   * - Does not scale well beyond ~20-30 repositories
   * - No server-side pagination, filtering, or sorting
   * - High memory usage on client for large datasets
   *
   * Recommended backend endpoint should support:
   * - GET /api/scans?page=1&limit=20&status=completed&sort_by=created_at
   * - Server-side aggregation and pagination
   *
   * @returns {Promise} - Promise with all scans
   */
  getAllScans: async () => {
    try {
      // First, get all repositories
      const reposResponse = await apiClient.get('/repositories');
      // Handle both { repositories: [...] } and direct array response formats
      const repositories = Array.isArray(reposResponse.data)
        ? reposResponse.data
        : (reposResponse.data.repositories || []);

      // Fetch scans for each repository in parallel (mitigates but doesn't solve N+1)
      const allScansPromises = repositories.map(async (repo) => {
        try {
          const scansResponse = await apiClient.get(
            `/repositories/scans?repo_url=${encodeURIComponent(repo.repo_url)}`
          );
          // Handle both { scans: [...] } and direct array response formats
          const scans = Array.isArray(scansResponse.data)
            ? scansResponse.data
            : (scansResponse.data.scans || []);

          // Add repo_url to each scan for display
          return scans.map(scan => ({
            ...scan,
            repo_url: repo.repo_url
          }));
        } catch (error) {
          console.error(`Error fetching scans for ${repo.repo_url}:`, error);
          return []; // Return empty array if this repo's scans fail
        }
      });

      // Wait for all scan fetches to complete
      const scansArrays = await Promise.all(allScansPromises);

      // Flatten the array of arrays into a single array
      const allScans = scansArrays.flat();

      return allScans;
    } catch (error) {
      console.error('Error fetching all scans:', error);
      throw error;
    }
  },


  validateRepository: async (repoUrl, isPrivateCheckboxChecked) => {
    try {
      const response = await apiClient.post("/validate-repository", {
        repoUrl,
        isPrivateCheckboxChecked,
      });
      // If the backend sends a 200 OK with a specific status field (e.g., NOT_FOUND),
      // that data will be returned here.
      return response.data;
    } catch (error) {
      console.error(
        "Error validating repository:",
        error.response ? error.response.data : error.message
      );
      if (error.response && error.response.data) {
        throw error.response.data;
      }
      // For network errors or other issues where error.response is not available or doesn't have data
      throw new Error(
        error.message ||
          "Network error or unexpected issue during repository validation."
      );
    }
  },

  /**
   * Get the current authenticated user
   * @returns {Promise} - Promise with the user data
   */
  getCurrentUser: async () => {
    try {
      const response = await apiClient.get('/auth/user');
      return response.data;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  },

  /**
   * Get recently merged pull requests for the dashboard
   * @param {number} limit - Number of PRs to return
   * @param {string} startDate - Optional start date in YYYY-MM-DD format
   * @param {string} endDate - Optional end date in YYYY-MM-DD format
   * @returns {Promise} - Promise with merged PRs data
   */
  getMergedPullRequests: async (limit = 10, startDate = null, endDate = null) => {
    try {
      const params = { limit };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await apiClient.get("/dashboard/merged-pull-requests", { params });
      return response.data;
    } catch (error) {
      console.error("Error getting merged pull requests:", error);
      throw error;
    }
  },

  /**
   * Get published blog posts
   * @param {number} limit - Number of posts to return
   * @param {number} offset - Pagination offset
   * @param {string} severity - Filter by severity (optional)
   * @param {string} tag - Filter by tag (optional)
   * @returns {Promise} - Promise with blog posts data
   */
  getBlogPosts: async (limit = 10, offset = 0, severity = null, tag = null) => {
    try {
      const params = { limit, offset };
      if (severity) params.severity = severity;
      if (tag) params.tag = tag;
      const response = await apiClient.get('/blog-posts', { params });
      return response.data;
    } catch (error) {
      console.error("Error getting blog posts:", error);
      throw error;
    }
  },

  /**
   * Get a specific blog post by slug
   * @param {string} slug - URL-friendly slug
   * @returns {Promise} - Promise with blog post data
   */
  getBlogPost: async (slug) => {
    try {
      const response = await apiClient.get(`/blog-posts/${slug}`);
      return response.data;
    } catch (error) {
      console.error("Error getting blog post:", error);
      throw error;
    }
  },

  // =============================================
  // Gap Analysis Dashboard & Task Management APIs (Issue #6)
  // =============================================

  /**
   * Get dashboard data for a compliance project
   * @param {string} projectId - Project UUID
   * @returns {Promise} - Promise with dashboard data including progress, TSC categories, and high priority tasks
   */
  getDashboard: async (projectId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const response = await apiClient.get(`/compliance/projects/${safeProjectId}/dashboard`);
      return response.data;
    } catch (error) {
      console.error("Error getting project dashboard:", error);
      throw error;
    }
  },

  /**
   * Get paginated and filtered tasks for a compliance project
   * @param {string} projectId - Project UUID
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (1-indexed)
   * @param {number} options.pageSize - Number of items per page
   * @param {string} options.sortBy - Sort field (priority, status, control_id)
   * @param {string} options.sortOrder - Sort direction (asc, desc)
   * @param {string} options.status - Filter by status (auto_verified, manually_verified, pending, failed)
   * @param {string} options.priority - Filter by priority (critical, high, medium, low)
   * @param {string} options.category - Filter by TSC category
   * @param {string} options.integrationSource - Filter by integration source (aws, google_workspace, github)
   * @returns {Promise} - Promise with paginated tasks data
   */
  getProjectTasks: async (projectId, options = {}) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const {
        page = 1,
        pageSize = 25,
        sortBy = 'priority',
        sortOrder = 'desc',
        status,
        priority,
        category,
        integrationSource,
        evidenceTier,
      } = options;

      const params = {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (status) params.status = status;
      if (priority) params.priority = priority;
      if (category) params.category = category;
      if (integrationSource) params.integration_source = integrationSource;
      if (evidenceTier) params.evidence_tier = evidenceTier;

      const response = await apiClient.get(`/compliance/projects/${safeProjectId}/tasks`, { params });
      return response.data;
    } catch (error) {
      console.error("Error getting project tasks:", error);
      throw error;
    }
  },

  /**
   * Update a specific task in a compliance project
   * @param {string} projectId - Project UUID
   * @param {string} taskId - Task UUID
   * @param {Object} updates - Fields to update
   * @param {string} updates.status - New status (manually_verified, not_applicable, pending, failed)
   * @param {string} updates.notes - Optional notes
   * @param {string} updates.evidence_url - Optional evidence URL
   * @returns {Promise} - Promise with updated task data
   */
  updateProjectTask: async (projectId, taskId, updates) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const safeTaskId = sanitizePathParam(taskId);
      const response = await apiClient.patch(
        `/compliance/projects/${safeProjectId}/tasks/${safeTaskId}`,
        updates
      );
      return response.data;
    } catch (error) {
      console.error("Error updating project task:", error);
      throw error;
    }
  },

  // =============================================
  // Evidence Upload APIs (Issue #9)
  // =============================================

  /**
   * Get detailed information for a specific task
   * @param {string} projectId - Project UUID
   * @param {string} taskId - Task UUID
   * @returns {Promise} - Promise with task detail including evidence
   */
  getTaskDetail: async (projectId, taskId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const safeTaskId = sanitizePathParam(taskId);
      const response = await apiClient.get(
        `/compliance/projects/${safeProjectId}/tasks/${safeTaskId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting task detail:", error);
      throw error;
    }
  },

  /**
   * Upload file evidence for a task
   * @param {string} projectId - Project UUID
   * @param {string} taskId - Task UUID
   * @param {File} file - File to upload
   * @param {string} type - Evidence type (e.g., 'screenshot', 'document', 'configuration')
   * @param {string} title - Title/description of the evidence
   * @returns {Promise} - Promise with uploaded evidence data
   */
  uploadFileEvidence: async (projectId, taskId, file, type, title) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const safeTaskId = sanitizePathParam(taskId);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      formData.append('title', title);
      const response = await apiClient.post(
        `/compliance/projects/${safeProjectId}/tasks/${safeTaskId}/evidence/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading file evidence:", error);
      throw error;
    }
  },

  /**
   * Create text-based evidence for a task
   * @param {string} projectId - Project UUID
   * @param {string} taskId - Task UUID
   * @param {Object} data - Evidence data
   * @param {string} data.type - Evidence type (e.g., 'note', 'procedure', 'configuration')
   * @param {string} data.title - Title/description of the evidence
   * @param {string} data.content - Text content of the evidence
   * @returns {Promise} - Promise with created evidence data
   */
  createTextEvidence: async (projectId, taskId, { type, title, content }) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const safeTaskId = sanitizePathParam(taskId);
      const response = await apiClient.post(
        `/compliance/projects/${safeProjectId}/tasks/${safeTaskId}/evidence`,
        { type, title, content }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating text evidence:", error);
      throw error;
    }
  },

  /**
   * Delete evidence from a task
   * @param {string} projectId - Project UUID
   * @param {string} taskId - Task UUID
   * @param {string} evidenceId - Evidence UUID
   * @returns {Promise} - Promise with deletion result
   */
  deleteEvidence: async (projectId, taskId, evidenceId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const safeTaskId = sanitizePathParam(taskId);
      const safeEvidenceId = sanitizePathParam(evidenceId);
      const response = await apiClient.delete(
        `/compliance/projects/${safeProjectId}/tasks/${safeTaskId}/evidence/${safeEvidenceId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting evidence:", error);
      throw error;
    }
  },

  /**
   * Get download URL for evidence file
   * @param {string} projectId - Project UUID
   * @param {string} taskId - Task UUID
   * @param {string} evidenceId - Evidence UUID
   * @returns {string} - Download URL for the evidence file
   */
  getEvidenceDownloadUrl: (projectId, taskId, evidenceId) => {
    const safeProjectId = sanitizePathParam(projectId);
    const safeTaskId = sanitizePathParam(taskId);
    const safeEvidenceId = sanitizePathParam(evidenceId);
    const baseUrl = apiClient.defaults.baseURL || '';
    return `${baseUrl}/compliance/projects/${safeProjectId}/tasks/${safeTaskId}/evidence/${safeEvidenceId}/download`;
  },

  // =============================================
  // Policy Management APIs (Issue #8)
  // =============================================

  listProjectPolicies: async (projectId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const response = await apiClient.get(
        `/compliance/projects/${safeProjectId}/policies`
      );
      return response.data;
    } catch (error) {
      console.error("Error listing project policies:", error);
      throw error;
    }
  },

  getProjectPolicy: async (projectId, policyId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const safePolicyId = sanitizePathParam(policyId);
      const response = await apiClient.get(
        `/compliance/projects/${safeProjectId}/policies/${safePolicyId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting project policy:", error);
      throw error;
    }
  },

  updateProjectPolicy: async (projectId, policyId, data) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const safePolicyId = sanitizePathParam(policyId);
      const response = await apiClient.put(
        `/compliance/projects/${safeProjectId}/policies/${safePolicyId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error updating project policy:", error);
      throw error;
    }
  },

  approveProjectPolicy: async (projectId, policyId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const safePolicyId = sanitizePathParam(policyId);
      const response = await apiClient.post(
        `/compliance/projects/${safeProjectId}/policies/${safePolicyId}/approve`
      );
      return response.data;
    } catch (error) {
      console.error("Error approving project policy:", error);
      throw error;
    }
  },

  generateAllPolicies: async (projectId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const response = await apiClient.post(
        `/compliance/projects/${safeProjectId}/policies/generate-all`,
        {},
        { timeout: 300000 }
      );
      return response.data;
    } catch (error) {
      console.error("Error generating all policies:", error);
      throw error;
    }
  },

  generatePolicy: async (projectId, policyType) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const response = await apiClient.post(
        `/compliance/projects/${safeProjectId}/policies/generate`,
        { policy_type: policyType },
        { timeout: 300000 }
      );
      return response.data;
    } catch (error) {
      console.error("Error generating policy:", error);
      throw error;
    }
  },

  getPolicyGenerationStatus: async (projectId) => {
    try {
      const safeProjectId = sanitizePathParam(projectId);
      const response = await apiClient.get(
        `/compliance/projects/${safeProjectId}/policies/generation-status`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting policy generation status:", error);
      throw error;
    }
  },

  listPolicyClaims: async (projectId, policyId) => {
    const safeProjectId = sanitizePathParam(projectId);
    const safePolicyId = sanitizePathParam(policyId);
    try {
      const response = await apiClient.get(
        `/compliance/projects/${safeProjectId}/policies/${safePolicyId}/claims`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching policy claims:', error);
      throw error;
    }
  },

  updatePolicyClaim: async (projectId, policyId, claimId, data) => {
    const safeProjectId = sanitizePathParam(projectId);
    const safePolicyId = sanitizePathParam(policyId);
    const safeClaimId = sanitizePathParam(claimId);
    try {
      const response = await apiClient.patch(
        `/compliance/projects/${safeProjectId}/policies/${safePolicyId}/claims/${safeClaimId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error updating policy claim:', error);
      throw error;
    }
  },

  // ============================================================================
  // Risk Assessment
  // ============================================================================

  generateRiskAssessment: async (projectId) => {
    const safeProjectId = sanitizePathParam(projectId);
    try {
      const response = await apiClient.post(
        `/compliance/projects/${safeProjectId}/risk-assessment/generate`
      );
      return response.data;
    } catch (error) {
      console.error('Error generating risk assessment:', error);
      throw error;
    }
  },

  getRiskAssessment: async (projectId) => {
    const safeProjectId = sanitizePathParam(projectId);
    const response = await apiClient.get(
      `/compliance/projects/${safeProjectId}/risk-assessment`
    );
    return response.data;
  },

  updateRisk: async (projectId, riskId, data) => {
    const safeProjectId = sanitizePathParam(projectId);
    const safeRiskId = sanitizePathParam(riskId);
    try {
      const response = await apiClient.put(
        `/compliance/projects/${safeProjectId}/risk-assessment/risks/${safeRiskId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error updating risk:', error);
      throw error;
    }
  },

  createRisk: async (projectId, data) => {
    const safeProjectId = sanitizePathParam(projectId);
    try {
      const response = await apiClient.post(
        `/compliance/projects/${safeProjectId}/risk-assessment/risks`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error creating risk:', error);
      throw error;
    }
  },

  deleteRisk: async (projectId, riskId) => {
    const safeProjectId = sanitizePathParam(projectId);
    const safeRiskId = sanitizePathParam(riskId);
    try {
      await apiClient.delete(
        `/compliance/projects/${safeProjectId}/risk-assessment/risks/${safeRiskId}`
      );
    } catch (error) {
      console.error('Error deleting risk:', error);
      throw error;
    }
  },

  approveRiskAssessment: async (projectId) => {
    const safeProjectId = sanitizePathParam(projectId);
    try {
      const response = await apiClient.post(
        `/compliance/projects/${safeProjectId}/risk-assessment/approve`
      );
      return response.data;
    } catch (error) {
      console.error('Error approving risk assessment:', error);
      throw error;
    }
  },

  getSuggestedControls: async (projectId, riskId) => {
    const safeProjectId = sanitizePathParam(projectId);
    const safeRiskId = sanitizePathParam(riskId);
    try {
      const response = await apiClient.get(
        `/compliance/projects/${safeProjectId}/risk-assessment/risks/${safeRiskId}/suggested-controls`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching suggested controls:', error);
      throw error;
    }
  },

  // ============================================================================
  // Audit Export APIs (Issue #17)
  // ============================================================================

  /**
   * Get export warnings for a compliance project
   * @param {string} projectId - Project UUID
   * @returns {Promise} - Promise with warnings object containing critical, warnings, and info arrays
   */
  getExportWarnings: async (projectId) => {
    const safeProjectId = sanitizePathParam(projectId);
    const response = await apiClient.get(
      `/compliance/projects/${safeProjectId}/export/warnings`
    );
    return response.data;
  },

  /**
   * Download audit export package for a compliance project
   * @param {string} projectId - Project UUID
   * @returns {Promise} - Promise with blob response containing the ZIP file
   */
  downloadExportPackage: async (projectId) => {
    const safeProjectId = sanitizePathParam(projectId);
    const response = await apiClient.post(
      `/compliance/projects/${safeProjectId}/export`,
      {},
      { responseType: 'blob', timeout: 300000 }
    );
    return response;
  },

  // ============================================================================
  // Trust Center APIs
  // ============================================================================

  /**
   * Get trust center settings for a compliance project
   * @param {string} projectId - Project UUID
   * @returns {Promise} - Promise with trust center settings
   */
  getTrustCenterSettings: async (projectId) => {
    const safeProjectId = sanitizePathParam(projectId);
    const response = await apiClient.get(`/compliance/projects/${safeProjectId}/trust-center`);
    return response.data;
  },

  /**
   * Update trust center settings for a compliance project
   * @param {string} projectId - Project UUID
   * @param {Object} settings - Trust center settings to update
   * @returns {Promise} - Promise with updated settings
   */
  updateTrustCenterSettings: async (projectId, settings) => {
    const safeProjectId = sanitizePathParam(projectId);
    const response = await apiClient.put(`/compliance/projects/${safeProjectId}/trust-center`, settings);
    return response.data;
  },

  /**
   * Get trust center preview for a compliance project
   * @param {string} projectId - Project UUID
   * @returns {Promise} - Promise with trust center preview data
   */
  getTrustCenterPreview: async (projectId) => {
    const safeProjectId = sanitizePathParam(projectId);
    const response = await apiClient.get(`/compliance/projects/${safeProjectId}/trust-center/preview`);
    return response.data;
  },

  /**
   * Get public trust center by slug (no auth required)
   * @param {string} slug - Trust center slug
   * @returns {Promise} - Promise with public trust center data
   */
  getTrustCenterPublic: async (slug) => {
    const safeSlug = sanitizePathParam(slug);
    const response = await apiClient.get(`/trust-center/${safeSlug}`);
    return response.data;
  },

  // ============================================================================
  // Structured Evidence APIs (Anti-Rubber-Stamp Controls)
  // ============================================================================

  /**
   * Get structured evidence schema and existing submission for a task
   * @param {string} projectId - Project UUID
   * @param {string} taskId - Task UUID
   * @returns {Promise} - Promise with schema and submission data
   */
  getStructuredEvidence: async (projectId, taskId) => {
    const safeProjectId = sanitizePathParam(projectId);
    const safeTaskId = sanitizePathParam(taskId);
    const response = await apiClient.get(
      `/compliance/projects/${safeProjectId}/tasks/${safeTaskId}/structured-evidence`
    );
    return response.data;
  },

  /**
   * Submit structured evidence (draft or final) for a task
   * @param {string} projectId - Project UUID
   * @param {string} taskId - Task UUID
   * @param {Object} fieldValues - Map of field keys to values
   * @param {boolean} isDraft - Whether this is a draft save (skips validation)
   * @returns {Promise} - Promise with submission result
   */
  submitStructuredEvidence: async (projectId, taskId, fieldValues, isDraft = false) => {
    const safeProjectId = sanitizePathParam(projectId);
    const safeTaskId = sanitizePathParam(taskId);
    const response = await apiClient.put(
      `/compliance/projects/${safeProjectId}/tasks/${safeTaskId}/structured-evidence`,
      { field_values: fieldValues, is_draft: isDraft }
    );
    return response.data;
  },

  // ============================================================================
  // Applications / Pentest APIs (Shannon dynamic-analysis integration)
  // ============================================================================

  /**
   * List all pentest applications for the current user.
   * @returns {Promise} - Promise with the list of applications
   */
  getApplications: async () => {
    try {
      const response = await apiClient.get('/applications');
      return response.data;
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }
  },

  /**
   * Create a new pentest application.
   * repoUrl is required by the backend to START a pentest (Shannon is white-box only, its -r
   * flag is mandatory), though the create endpoint itself still accepts one without.
   * @param {Object} data - { name, repoUrl? }
   * @returns {Promise} - Promise with the created application
   */
  createApplication: async ({ name, repoUrl } = {}) => {
    try {
      const payload = { name };
      if (repoUrl) payload.repoUrl = repoUrl;
      const response = await apiClient.post('/applications', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating application:', error);
      throw error;
    }
  },

  /**
   * Update an application's name and/or linked repository URL.
   * Exists so an application created without a repoUrl can have one added without recreating it
   * and re-running ownership verification on its targets.
   * @param {string} appId - Application ID
   * @param {Object} data - { name?, repoUrl? }
   * @returns {Promise} - Promise with the updated application
   */
  updateApplication: async (appId, { name, repoUrl } = {}) => {
    try {
      const safeAppId = sanitizePathParam(String(appId));
      const payload = {};
      if (name !== undefined) payload.name = name;
      if (repoUrl !== undefined) payload.repoUrl = repoUrl;
      const response = await apiClient.put(`/applications/${safeAppId}`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating application:', error);
      throw error;
    }
  },

  /**
   * Create a target (a running URL) under an application.
   * @param {string} appId - Application ID
   * @param {Object} data - { targetUrl, environment }
   * @returns {Promise} - Promise with the created target
   */
  createTarget: async (appId, { targetUrl, environment } = {}) => {
    try {
      const safeAppId = sanitizePathParam(String(appId));
      const response = await apiClient.post(`/applications/${safeAppId}/targets`, {
        targetUrl,
        environment,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating target:', error);
      throw error;
    }
  },

  /**
   * Begin ownership verification for a target.
   * @param {string} targetId - Target ID
   * @returns {Promise} - Promise with { method, token, instructions }
   */
  startTargetVerification: async (targetId) => {
    try {
      const safeTargetId = sanitizePathParam(String(targetId));
      const response = await apiClient.post(
        `/applications/targets/${safeTargetId}/verify/start`
      );
      return response.data;
    } catch (error) {
      console.error('Error starting target verification:', error);
      throw error;
    }
  },

  /**
   * Check ownership verification for a target.
   * @param {string} targetId - Target ID
   * @returns {Promise} - Promise with { verified }
   */
  checkTargetVerification: async (targetId) => {
    try {
      const safeTargetId = sanitizePathParam(String(targetId));
      const response = await apiClient.post(
        `/applications/targets/${safeTargetId}/verify/check`
      );
      return response.data;
    } catch (error) {
      console.error('Error checking target verification:', error);
      throw error;
    }
  },

  /**
   * Start an autonomous pentest.
   * @param {Object} payload - { applicationId, targetId, scope:{avoid:[],focus:[]},
   *                             rateLimit, auth:{loginUrl,username,password}, attestation }
   * @returns {Promise} - Promise with { pentestId }
   */
  startPentest: async (payload) => {
    try {
      const response = await apiClient.post('/pentest/start', payload);
      return response.data;
    } catch (error) {
      console.error('Error starting pentest:', error);
      throw error;
    }
  },

  /**
   * Get the current status of a running pentest.
   * @param {string} pentestId - Pentest ID
   * @returns {Promise} - Promise with { status, currentPhase, progress, logs }
   */
  getPentestStatus: async (pentestId, lastLogId = 0) => {
    try {
      const safePentestId = sanitizePathParam(String(pentestId));
      const response = await apiClient.get(`/pentest/${safePentestId}/status`, {
        // lastLogId makes log delivery incremental. Without it the endpoint re-sends the whole
        // accumulated log on every poll, which over a ~1-1.5h run meant hundreds of requests
        // and tens of MB (observed: 334 requests / 22.6 MB, ending in a polling failure).
        params: { last_log_id: lastLogId },
        timeout: 30000,
      });
      return response.data;
    } catch (error) {
      console.error('Error getting pentest status:', error);
      throw error;
    }
  },

  /**
   * Get the findings for a completed pentest.
   * @param {string} pentestId - Pentest ID
   * @returns {Promise} - Promise with the findings
   */
  getPentestFindings: async (pentestId) => {
    try {
      const safePentestId = sanitizePathParam(String(pentestId));
      const response = await apiClient.get(`/pentest/${safePentestId}/findings`);
      return response.data;
    } catch (error) {
      console.error('Error getting pentest findings:', error);
      throw error;
    }
  },

  /**
   * Get the downloadable report URL for a pentest.
   * @param {string} pentestId - Pentest ID
   * @returns {Promise} - Promise with { reportUrl }
   */
  getPentestReport: async (pentestId) => {
    try {
      const safePentestId = sanitizePathParam(String(pentestId));
      const response = await apiClient.get(`/pentest/${safePentestId}/report`);
      return response.data;
    } catch (error) {
      console.error('Error getting pentest report:', error);
      throw error;
    }
  },

  /**
   * Absolute URL for a pentest's PDF download.
   * Built from the configured API base, NOT from the server's relative path: the UI and API are
   * on different origins (app. vs appb.), so a relative href would resolve against the UI's
   * origin and 404. Mirrors getComplianceEvidenceDownloadUrl.
   * @param {string} pentestId - Pentest ID
   * @returns {string} - Absolute download URL
   */
  getPentestReportDownloadUrl: (pentestId) => {
    const safePentestId = sanitizePathParam(String(pentestId));
    const baseUrl = apiClient.defaults.baseURL || '';
    return `${baseUrl}/pentest/${safePentestId}/report/download`;
  },

  /**
   * Cancel a queued or running pentest.
   * A queued run stops immediately. A running one is cooperative: the backend marks the session
   * cancelled and the worker kills the engine within ~15s, so the response reports
   * `engineStopping` rather than implying it has already stopped.
   * @param {string} pentestId - Pentest ID
   * @returns {Promise} - Promise with { status, previousStatus, engineStopping }
   */
  cancelPentest: async (pentestId) => {
    try {
      const safePentestId = sanitizePathParam(String(pentestId));
      const response = await apiClient.post(`/pentest/${safePentestId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling pentest:', error);
      throw error;
    }
  },
};

export default securityApi;
